import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { t } from "@/i18n";

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (q.trim().length < 2) return setGroups([]);
    const id = setTimeout(() => api.get("/search", { params: { q } }).then(({ data }) => setGroups(data.groups)).catch(() => setGroups([])), 250);
    return () => clearTimeout(id);
  }, [q]);
  const go = (to) => { setOpen(false); setQ(""); navigate(to); };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-2 text-slate-500" data-testid="global-search-button" aria-label={t("b8.search.title")}>
        <Search className="h-4 w-4" /><span className="hidden lg:inline">{t("b8.search.placeholder")}</span><kbd className="hidden rounded bg-slate-100 px-1.5 text-[10px] lg:inline">Ctrl K</kbd>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="top-[10%] max-w-xl translate-y-0 gap-0 p-0" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">{t("b8.search.title")}</DialogTitle>
          <div className="flex items-center gap-2 border-b border-slate-200 px-4"><Search className="h-4 w-4 text-slate-400" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("b8.search.placeholder")} className="h-12 border-0 shadow-none focus-visible:ring-0" data-testid="global-search-input" />
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-2" data-testid="global-search-results">
            {q.trim().length >= 2 && groups.length === 0 && <p className="p-4 text-sm text-slate-500" data-testid="global-search-empty">{t("b8.search.empty")}</p>}
            {groups.map((g) => (
              <div key={g.kind} className="mb-2" data-testid={`search-group-${g.kind}`}>
                <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t(`b8.search.kinds.${g.kind}`)}</p>
                {g.items.map((it) => (
                  <button key={it.id} type="button" onClick={() => go(it.to)} className="flex w-full flex-col rounded-md px-3 py-2 text-left transition-colors duration-150 hover:bg-slate-100" data-testid={`search-result-${g.kind}-${it.id}`}>
                    <span className="text-sm font-semibold text-slate-900">{it.title}</span>{it.subtitle && <span className="text-xs text-slate-500">{it.subtitle}</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
